// src/components/devices/LockControl.js
import React, { useEffect, useRef, useState } from 'react';

const HOLD_DURATION = 700;

/**
 * LockControl
 *
 * Minimal UX upgrade:
 * - Added `processing` prop: 'locking' | 'unlocking' | null
 * - When processing, show an obvious spinner ring + subtle pulsing halo (Apple/Tesla feel)
 * - Block repeat taps while processing
 * - Keeps your existing tap/hold behavior intact
 *
 * Fix in this version:
 * - Icon inside circle is larger (better proportions)
 * - Spinner ring inset reduced so it doesn't steal visual space
 */
const LockControl = ({
  isLocked,
  onLock,
  onUnlock,
  disabled,
  processing = null,
  className = ''
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressIntervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const actionFiredRef = useRef(false);

  const isProcessing = !!processing;

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  // If we enter processing, cancel any hold animation to avoid visual conflicts.
  useEffect(() => {
    if (!isProcessing) return;
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    startTimeRef.current = null;
    setIsPressed(false);
    setProgress(0);
    actionFiredRef.current = false;
  }, [isProcessing]);

  const triggerHaptic = () => {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(50);
    }
  };

  const safeTriggerAction = () => {
    if (disabled || isProcessing) return;
    triggerHaptic();
    if (isLocked) {
      onUnlock();
    } else {
      onLock();
    }
  };

  const startHold = (e) => {
    e.stopPropagation();
    if (disabled || isProcessing) return;

    actionFiredRef.current = false;
    startTimeRef.current = Date.now();
    setIsPressed(true);
    setProgress(0);

    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);

    progressIntervalRef.current = setInterval(() => {
      if (!startTimeRef.current) return;

      const elapsed = Date.now() - startTimeRef.current;
      const newProgress = Math.min((elapsed / HOLD_DURATION) * 100, 100);
      setProgress(newProgress);

      if (newProgress >= 100 && !actionFiredRef.current) {
        actionFiredRef.current = true;
        clearInterval(progressIntervalRef.current);
        setIsPressed(false);
        safeTriggerAction();
        setTimeout(() => setProgress(0), 100);
      }
    }, 16);
  };

  const endHold = (e) => {
    e.stopPropagation();
    if (disabled || isProcessing || startTimeRef.current === null) return;

    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    const elapsed = Date.now() - startTimeRef.current;

    setIsPressed(false);
    setTimeout(() => setProgress(0), 100);

    // Quick tap triggers immediately (existing behavior)
    if (elapsed < HOLD_DURATION && !actionFiredRef.current) {
      actionFiredRef.current = true;
      safeTriggerAction();
    }

    startTimeRef.current = null;
  };

  const handleMouseLeave = (e) => {
    if (isPressed) {
      endHold(e);
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (disabled || isProcessing || isPressed || actionFiredRef.current) return;

    actionFiredRef.current = true;
    safeTriggerAction();
  };

  // ===== Size logic =====
  const size = className.match(/w-(\d+)/)?.[1] || 14;
  const sizeNum = parseInt(size, 10);

  // FIX: bigger icon ratio (was 0.65)
  const iconSize = sizeNum * 0.98;

  const radius = (sizeNum / 2) - 6;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress / 100);

  const baseBg = isProcessing
    ? 'bg-neutral-100'
    : isLocked
    ? 'bg-emerald-100 hover:bg-emerald-200'
    : 'bg-rose-100 hover:bg-rose-200';

  return (
    <div
      className={`lock-control relative flex items-center justify-center rounded-full transition-all duration-200 ${baseBg} ${
        (disabled || isProcessing) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer active:scale-95'
      } ${className}`}
      onClick={handleClick}
      onTouchStart={startHold}
      onTouchEnd={endHold}
      onMouseDown={startHold}
      onMouseUp={endHold}
      onMouseLeave={handleMouseLeave}
      role="button"
      aria-disabled={disabled || isProcessing}
    >
      {/* Subtle pulsing halo while processing */}
      {isProcessing && (
        <div
          className="
            absolute inset-0 rounded-full
            animate-pulse
            bg-neutral-200/40
          "
          aria-hidden="true"
        />
      )}

      {/* Single processing ring */}
      {isProcessing && (
        <div
          className="
            absolute inset-1 rounded-full
            border border-neutral-300
            border-t-neutral-700
            animate-spin
          "
          aria-hidden="true"
        />
      )}

      {/* ICON (scaled slightly for better fill) */}
      <div className="relative z-10 scale-[1.12]">
        {isLocked ? (
          <svg
            className={isProcessing ? 'text-neutral-700' : 'text-emerald-700'}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            width={iconSize}
            height={iconSize}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        ) : (
          <svg
            className={isProcessing ? 'text-neutral-700' : 'text-rose-700'}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            width={iconSize}
            height={iconSize}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
            />
          </svg>
        )}
      </div>

      {/* Existing hold-to-confirm progress ring (kept) */}
      {isPressed && !isLocked && !isProcessing && (
        <svg
          className="absolute inset-0 -rotate-90"
          viewBox={`0 0 ${sizeNum} ${sizeNum}`}
        >
          <circle
            cx={sizeNum / 2}
            cy={sizeNum / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="text-gray-300"
          />
          <circle
            cx={sizeNum / 2}
            cy={sizeNum / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="text-red-600 transition-none"
            style={{ transition: progress === 0 ? 'none' : 'stroke-dashoffset 0.1s linear' }}
          />
        </svg>
      )}
    </div>
  );
};

export default LockControl;
